import React, { useCallback, useEffect, useState } from "react";
import { Avatar, Divider, Grid, Link, Stack, Table, TableBody, TableCell, TableRow } from "@mzc-pdc/ui";
import convertToByte from "@cores/convertToByte";
import Typography from "@components_v2/typography/typography";
import theme from "@styles/theme";
import { IconArchive, IconOpenInNew } from "@mzc-cloudplex/icons";
import TableCard from "@components_v2/card/table-card";
import CollectionThumbnail from "@features/thumbnail/collection-thumbnail";
import AssetThumbnail from "@features/thumbnail/asset-thumbnail";
import { useInView } from "react-intersection-observer";
import StatusBadge from "@components_v2/status-badge/status-badge";
import { useTranslation } from "react-i18next";
import { getProjectRandomColor } from "@utils/get-random-color";
import { Link as RouterLink } from "react-router-dom";
import formatDuration from "@cores/formatDuration";
import AssetTypeFlag from "@components_v2/icon-type/asset/asset-type-flag";
import ContentTypeFlag from "@components_v2/icon-type/content/content-type-flag";
import { ASSET_PREVIEW_STATUS, MEDIA_TYPES } from "@constants";
import BadgeCollection from "@components_v2/badge-collection/badge-collection";
import MediaImageWrapper from "@routes/asset/components/media-image-wrapper";
import { alpha } from "@mui/material";
import ContentMediaImage from "@features/content-media-image/content-media-image";
import { EllipsisTypography, OverflowTooltip } from "@components_v2/ellipsis-tooltip";

const AutocompleteSmallTableList = ({
    data,
    columns,
    subNameIds,
    type,
    deleteButton,
    onClickName,
    listStyle,
    disableThumbnail = false,
    onFetchData = () => {},
    noDataText,
    gridStyle,
}) => {
    const { t } = useTranslation();
    const [targetRef, inView] = useInView();
    const [tableWidth, setTableWidth] = useState(null);

    const tableRef = React.useRef();

    const getNameThumbnail = useCallback(
        (type, option, column) => {
            switch (type) {
                case "asset": {
                    let src;

                    const commonStatusPreviews = option?.previews?.filter(
                        (p) => p.status !== ASSET_PREVIEW_STATUS.ERROR,
                    );
                    const previewStatus =
                        commonStatusPreviews?.length === 0 && option.previews?.length > 0
                            ? ASSET_PREVIEW_STATUS.ERROR
                            : commonStatusPreviews?.[0]?.status ?? ASSET_PREVIEW_STATUS.COMPLETE;

                    if (commonStatusPreviews?.[0]?.accessUrl) src = commonStatusPreviews?.[0]?.accessUrl;
                    if (!src) {
                        if (option?.mediaType === MEDIA_TYPES.IMAGE) {
                            src = option.accessUrls?.[0] ?? option.elements?.[0]?.accessUrl;
                        } else if (option.mediaType === MEDIA_TYPES.VIDEO) src = option.thumbnails?.[0]?.accessUrl;
                    }

                    return (
                        <AssetThumbnail
                            variant={"table"}
                            src={src}
                            asset={option}
                            sx={{ ...column?.style?.thumbnail }}
                            errorIconProps={{ position: "left", size: 16 }}
                            previewStatus={previewStatus}
                        />
                    );
                }
                case "content": {
                    return (
                        <MediaImageWrapper
                            sx={{
                                position: `relative`,
                                width: 40,
                                height: 40,
                                borderRadius: `4px`,
                                backgroundColor: alpha("#e5e7ed", 0.5),
                                overflow: `hidden`,
                            }}
                        >
                            <ContentMediaImage
                                contentType={option.contentType}
                                variant={"table"}
                                errorIconProps={{ position: "left", size: 16 }}
                                data={option}
                                draggable={false}
                                isShowImagePreviewModal={false}
                            />
                        </MediaImageWrapper>
                    );
                }
                case "collection":
                    return <CollectionThumbnail collection={option} size={"xsmall"} variant={"table"} />;
            }
        },
        [data],
    );

    const getNameIcon = useCallback(
        (type, option) => {
            switch (type) {
                case "asset":
                    return <AssetTypeFlag size={"small"} type={option.mediaType} />;
                case "content":
                    return <ContentTypeFlag size={"small"} type={option.contentType} />;
            }
        },
        [data],
    );

    const getTableSubValueByColumn = useCallback(
        (id, option) => {
            switch (id) {
                case "extensions":
                    return (
                        <Typography variant={"body3_400"} component={"span"} color={theme.palette.secondary[600]}>
                            {option?.extensions ? `.${option?.extensions}` : "-"}
                        </Typography>
                    );
                case "duration":
                    return (
                        <Typography
                            variant={"body3_400"}
                            component={"span"}
                            color={theme.palette.secondary[600]}
                            sx={{ minWidth: "120px" }}
                        >
                            {formatDuration(option?.duration)}
                        </Typography>
                    );
                case "mediaType":
                    return (
                        <Typography
                            variant={"body3_400"}
                            component={"span"}
                            color={theme.palette.secondary[600]}
                            sx={{ minWidth: "120px" }}
                        >
                            {option?.mediaType ?? "-"}
                        </Typography>
                    );
                case "type":
                    return (
                        <Typography
                            variant={"body3_400"}
                            component={"span"}
                            color={theme.palette.secondary[600]}
                            sx={{ minWidth: "120px" }}
                        >
                            {option?.type ?? "-"}
                        </Typography>
                    );
                case "size":
                    return (
                        <Typography
                            variant={"body3_400"}
                            component={"span"}
                            color={theme.palette.secondary[600]}
                            sx={{ minWidth: "80px" }}
                        >
                            {option?.size ? convertToByte(option?.size) : "-"}
                        </Typography>
                    );
                default:
                    return null;
            }
        },
        [data],
    );

    const getCustomCellWidth = useCallback(
        (column) => {
            if (!column.style?.custom) return "auto";
            const MENU_ACTION_WIDTH = deleteButton ? 50 : 0;
            if (!tableRef.current) return column.style.custom.width;
            if (column.id === "name") {
                const COLUMN_NAME_WIDTH = 200;
                const _tableWidth = tableWidth ?? tableRef.current.getBoundingClientRect().width;
                const cellWidth =
                    MENU_ACTION_WIDTH +
                    columns
                        .filter((el) => el.id !== "name")
                        .reduce((acc, cur) => acc + (cur.style.custom.width ?? 0), 0);
                if (_tableWidth - COLUMN_NAME_WIDTH <= cellWidth) return COLUMN_NAME_WIDTH;
                return `calc(100% - ${cellWidth}px - ${MENU_ACTION_WIDTH}px)`;
            }
            return column.style.custom.width;
        },
        [tableWidth],
    );

    const getTableValueByColumn = useCallback(
        (id, option, column) => {
            switch (id) {
                case "desiredStatus":
                case "status":
                    return (
                        <Grid container justifyContent={"center"}>
                            <StatusBadge status={option?.status?.toUpperCase()} />
                        </Grid>
                    );
                case "name":
                    return (
                        <TableCard
                            thumbnail={!disableThumbnail && getNameThumbnail(type, option, column)}
                            name={{
                                name: onClickName ? (
                                    <OverflowTooltip title={option.name}>
                                        <Link
                                            to={onClickName}
                                            style={{
                                                // 잘림 여부를 이 요소에서 측정하므로 말줄임 스타일을 직접 지정한다.
                                                // (Link 는 noWrap prop 을 지원하지 않는다)
                                                display: "block",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {option.name}
                                        </Link>
                                    </OverflowTooltip>
                                ) : (
                                    <EllipsisTypography variant={"body1_500"} component={"span"} noWrap>
                                        {option?.name}
                                    </EllipsisTypography>
                                ),
                                startIcon: getNameIcon(type, option),
                                endIcon:
                                    option.archiveStatus === "ARCHIVED" ? (
                                        <Grid item ml={1}>
                                            <IconArchive size={16} />
                                        </Grid>
                                    ) : undefined,
                            }}
                            subName={{
                                name: (
                                    <Grid
                                        container
                                        flexDirection={"row"}
                                        sx={{
                                            "& span:not(:last-child)": {
                                                pr: 5,
                                                borderRight: `solid 1px ${theme.palette.secondary[100]}`,
                                            },
                                            "& span:not(:first-of-type)": {
                                                pl: 1.5,
                                            },
                                        }}
                                    >
                                        {subNameIds?.map((id) => getTableSubValueByColumn(id, option))}
                                    </Grid>
                                ),
                            }}
                            sx={{ ...column.style?.thumbnail }}
                        />
                    );
                case "projectName":
                    return (
                        <Grid container flexDirection={"row"} alignItems={"center"}>
                            <Avatar
                                sx={{
                                    width: 32,
                                    height: 32,
                                    fontSize: 16,
                                    fontWeight: `bold`,
                                    bgColor: getProjectRandomColor(option?.id),
                                    mr: 1,
                                }}
                            >
                                {option.name.charAt(0)}
                            </Avatar>

                            {/* 1.0.81 Note 프로젝트 누르면 어디로 이동? */}
                            <OverflowTooltip title={option.name}>
                                <Link
                                    target="_blank"
                                    component={RouterLink}
                                    sx={{
                                        color: theme.palette.secondary[800],
                                        fontWeight: 500,
                                        display: `inline-flex`,
                                        WebkitLineClamp: `2`,
                                        textOverflow: `ellipsis`,
                                        WebkitBoxOrient: `vertical`,
                                        maxHeight: 45,
                                        overflow: `hidden`,
                                        textDecoration: "none",
                                        "&:hover": {
                                            "#external-link-icon": {
                                                display: "block",
                                            },
                                        },
                                    }}
                                    noWrap
                                    to={onClickName(option.id)}
                                >
                                    {option.name}
                                    <Grid item id={"external-link-icon"} sx={{ display: "none" }}>
                                        <IconOpenInNew size={16} />
                                    </Grid>
                                </Link>
                            </OverflowTooltip>
                        </Grid>
                    );
                case "size":
                    return (
                        <Grid item role={"size"}>
                            {option?.size >= 0 ? convertToByte(option?.size) ?? "-" : null}
                        </Grid>
                    );

                case "count":
                    return (
                        <Grid item sx={{ textAlign: `right` }} role={"count"}>
                            {option?.assetTotalCount >= 0 ? (
                                <BadgeCollection totalCount={option.assetTotalCount} />
                            ) : null}
                        </Grid>
                    );
                case "duration":
                    return (
                        <Grid item role={"duration"}>
                            {option?.[id] ? formatDuration(option?.duration) : null}
                        </Grid>
                    );
                case "extensions":
                    return (
                        <Grid item role={"extensions"}>
                            {option?.[id] ? `.${option?.[id]}` : null}
                        </Grid>
                    );
                default:
                    return option?.[id];
            }
        },
        [data],
    );

    useEffect(() => {
        if (!tableRef.current) return;
        let rafId;
        const resizeObserver = new ResizeObserver((entries) => {
            rafId = requestAnimationFrame(() => {
                for (const entry of entries) {
                    setTableWidth(entry.contentRect.width);
                }
            });
        });
        resizeObserver.observe(tableRef.current);

        return () => {
            cancelAnimationFrame(rafId);
            resizeObserver.disconnect();
        };
    }, [type]);

    useEffect(() => {
        if (inView) {
            onFetchData();
        }
    }, [inView, targetRef]);

    if (data?.length > 0)
        return (
            <Grid
                item
                sx={{
                    borderRadius: `4px`,
                    border: `1px solid ${theme.palette.secondary[100]}`,
                    overflowY: "auto",
                    // DAM-646: 스크롤바 생길 때 콘텐츠 밀림 방지
                    scrollbarGutter: "stable",
                    ...gridStyle,
                }}
            >
                <Table
                    sx={{
                        width: "100%",
                        padding: "0",
                        overflowY: "auto",
                        // DAM-646: 스크롤바 생길 때 콘텐츠 밀림 방지
                        scrollbarGutter: "stable",
                        tableLayout: "fixed",
                        ...listStyle,
                    }}
                >
                    <TableBody ref={tableRef}>
                        {data?.map((item, rowIndex) => (
                            <TableRow
                                key={`autocomplete-small-table-list-${item.id ?? rowIndex}`}
                                data-id={item.id ?? rowIndex}
                            >
                                {columns.map((column, index) => (
                                    <TableCell
                                        mt={1}
                                        mb={1}
                                        key={`autocomplete-small-table-list-${item.id ?? rowIndex}-${column.id}`}
                                        sx={{
                                            ...column.style.custom,
                                            width: getCustomCellWidth(column),
                                        }}
                                    >
                                        <Stack direction={"row"} alignItems="center" justifyContent="flex-start">
                                            {column.divider && index > 0 && (
                                                <Divider
                                                    orientation="vertical"
                                                    flexItem
                                                    style={{ marginRight: "16px" }}
                                                />
                                            )}
                                            {getTableValueByColumn(column.id, item, column)}
                                        </Stack>
                                    </TableCell>
                                ))}
                                {deleteButton && (
                                    <TableCell
                                        sx={{
                                            width: 50,
                                        }}
                                        align={"center"}
                                    >
                                        {deleteButton}
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {data && <div ref={targetRef} id={"loadMoreDiv"} />}
            </Grid>
        );
    if (data?.length === 0 && noDataText)
        return (
            <Grid
                container
                sx={{
                    height: "60px",
                    p: 1.5,
                    borderRadius: `4px`,
                    border: `1px solid ${theme.palette.secondary[100]}`,
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Typography>{t(`common::msg::${noDataText}`, noDataText)}</Typography>
            </Grid>
        );
    return null;
};

export default AutocompleteSmallTableList;
